/**
 * StudioLayoutBar — the layout-section command-bar controls.
 *
 * The old left-dock "Layout" tab (Parcel · Orientation · Packing · Yield search
 * · Exclusions) is now a row of command-bar popovers, freeing the left dock for
 * the Layers tree alone. Orientation / Packing / Yield share ONE
 * `useLayoutParameterSearch` state (the yield search sweeps the ranges set on
 * the Packing/Orientation rows), so the three popovers stay coupled even though
 * they open separately. Same handlers, same units (ft in the UI, metres in the
 * frozen layout) as the retired panel.
 */
import React from 'react';
import { Grid2x2, Route, SlidersHorizontal, Wand2, Wind } from 'lucide-react';

import type { BuildGenerationParcelTestFitInput, GenerationParcelTestFitResult } from '../../../../lib/generationImpact/generationLayoutGeometry';
import type { GenerationLayoutConfiguration } from '../../../../types/generationImpact';
import type { SiteBoundaryParcel } from '../../../../types/siteBoundary';
import type { SiteLayer } from '../../../../types/site';
import { GenerationLayoutExclusionControls } from '../GenerationLayoutExclusionControls';
import { LayoutParameterSections, useLayoutParameterSearch } from '../GenerationLayoutParameters';
import { StudioPopover } from './StudioPopover';

export interface StudioLayoutBarProps {
  testFit: GenerationParcelTestFitResult;
  parcels: readonly SiteBoundaryParcel[];
  activeLayout: GenerationLayoutConfiguration;
  readOnly: boolean;
  onPatch: (patch: Partial<GenerationLayoutConfiguration>) => void;
  onSelectParcel: (parcelId: string) => void;
  siteLayers: readonly SiteLayer[];
  sessionKey?: string;
  layoutSearchBase: Omit<BuildGenerationParcelTestFitInput, 'layout'>;
  /** Live placement state, shown as context so the panel explains the mode. */
  pinnedCount?: number;
  autoCount?: number;
  /** One-shot packer actions (formerly the separate "Assist" menu). */
  onCreateStartingLayout?: () => void;
  hasManualPlacements?: boolean;
  onPackSelection?: () => void;
  selectionCount?: number;
  onConnectRoads?: () => void;
  /** Sets row orientation from the wind rose — belongs with the other
   *  orientation inputs, not under Data/import-export. */
  onAlignCrosswind?: () => void;
  windAvailable?: boolean;
}

export const StudioLayoutBar: React.FC<StudioLayoutBarProps> = ({
  testFit,
  parcels,
  activeLayout,
  readOnly,
  onPatch,
  onSelectParcel,
  siteLayers,
  sessionKey,
  layoutSearchBase,
  pinnedCount = 0,
  autoCount = 0,
  onCreateStartingLayout,
  hasManualPlacements = false,
  onPackSelection,
  selectionCount = 0,
  onConnectRoads,
  onAlignCrosswind,
  windAvailable = false,
}) => {
  // One shared search state so the Packing/Orientation range toggles and the
  // Yield popover agree — even though they live in separate popovers.
  const search = useLayoutParameterSearch(layoutSearchBase, activeLayout, readOnly, onPatch);
  const exclusionCount = activeLayout.exclusions?.zones.length ?? 0;
  // Fully manual: the packer has nothing left to arrange, so scheme + yield
  // sweep are inert. Everything else in this panel still applies.
  const packerInert = pinnedCount > 0 && autoCount === 0;

  return (
    // One "Layout" popover with three sections. These are layout SETTINGS, not
    // commands — three separate command-bar buttons (plus a "LAYOUT" label) took
    // four slots on a bar that already overflowed. Orientation/Packing/Yield
    // already shared one `useLayoutParameterSearch` state, so nothing about the
    // coupling changes; only the number of triggers does.
    <div
      className="flex items-center border-r border-border/70 pr-2"
      role="group"
      aria-label="Layout sections"
      data-testid="studio-layout-bar"
    >
      <StudioPopover
        label="Auto-layout"
        surfaceLabel="Automatic layout — inputs, rules and actions"
        icon={SlidersHorizontal}
        variant="panel"
        active={exclusionCount > 0}
        testId="studio-autolayout-menu"
      >
        {() => (
          <div className="space-y-3">
            {/* Lead with the model, because the feature was not self-evident:
                the packer arranges equipment automatically from the parcel and
                rules below; pinning components takes manual control. */}
            <section aria-label="How automatic layout works" data-testid="studio-autolayout-explainer">
              <p className="text-[10px] leading-snug text-text-secondary">
                The packer arranges every component automatically inside the parcel below,
                following these rules. Pin a component (or switch Placement to
                <span className="font-bold"> Manual</span>) to place it by hand — the packer
                then flows the rest around it.
              </p>
              <p className="mt-1.5 text-[10px] font-bold uppercase tracking-wide tabular-nums text-text-muted">
                Now:{' '}
                {pinnedCount === 0
                  ? <span className="text-text-secondary">Auto — packing all {autoCount}</span>
                  : autoCount === 0
                    ? <span className="text-primary">Manual — all {pinnedCount} pinned</span>
                    : <span className="text-primary">{pinnedCount} pinned · {autoCount} auto</span>}
              </p>
            </section>

            <section aria-label="Target parcel" className="border-t border-border/70 pt-3" data-testid="studio-layout-parcel">
              <p className="pb-1 text-[9px] font-bold uppercase tracking-[0.18em] text-text-muted">Parcel</p>
              <label className="block text-[10px] uppercase text-text-muted">Target parcel
                <select
                  value={activeLayout.targetParcelId ?? ''}
                  onChange={(event) => onSelectParcel(event.target.value)}
                  disabled={readOnly || parcels.length === 0}
                  className="mt-1 w-full rounded-md border border-border bg-background p-2 text-xs normal-case text-text-primary disabled:opacity-50"
                  data-testid="studio-layout-parcel-select"
                >
                  {parcels.length === 0 && <option value="">No usable boundary</option>}
                  {parcels.map((parcel, index) => (
                    <option key={parcel.id} value={parcel.id}>{parcel.label ?? `Parcel ${index + 1}`} · {parcel.area.toFixed(1)} ac</option>
                  ))}
                </select>
              </label>
              {testFit.error && <p className="mt-2 border-l-2 border-danger pl-2 text-[10px] text-danger">{testFit.error}</p>}
            </section>

            <section aria-label="Layout parameters" className="border-t border-border/70 pt-3" data-testid="studio-layout-parameters">
              <LayoutParameterSections
                activeLayout={activeLayout}
                readOnly={readOnly}
                onPatch={onPatch}
                search={search}
                sections={['orientation', 'packing']}
                packerInert={packerInert}
              />
              {onAlignCrosswind && (
                // Row orientation has three setters (this, the Frontline draw
                // tool, and the orientation select). Crosswind alignment sat
                // under Data/import-export, far from the value it changes.
                <button
                  type="button"
                  disabled={readOnly || !windAvailable}
                  onClick={onAlignCrosswind}
                  title={windAvailable
                    ? 'Rotate rows perpendicular to the prevailing wind'
                    : 'Fetch wind data first (Overlays ▸ Fetch wind data)'}
                  className="mt-2 flex w-full items-center justify-center gap-1.5 rounded-md border border-border px-3 py-2 text-[9px] font-bold uppercase tracking-wider text-text-secondary transition-colors hover:border-text-muted/60 hover:text-text-primary disabled:cursor-not-allowed disabled:opacity-40"
                  data-testid="studio-autolayout-align-crosswind"
                >
                  <Wind className="h-3 w-3" aria-hidden />
                  Align rows crosswind
                </button>
              )}
            </section>

            {(onCreateStartingLayout || onPackSelection || onConnectRoads) && (
              <section aria-label="Automatic layout actions" className="border-t border-border/70 pt-3" data-testid="studio-autolayout-actions">
                <p className="pb-1.5 text-[9px] font-bold uppercase tracking-[0.18em] text-text-muted">Actions</p>
                <div className="space-y-1">
                  {onCreateStartingLayout && (
                    <button
                      type="button"
                      disabled={readOnly}
                      onClick={onCreateStartingLayout}
                      title={hasManualPlacements
                        ? 'Re-pack every component from the rules above, clearing all pins'
                        : 'Pack every component from the rules above'}
                      className="flex w-full items-center gap-2 rounded-md border border-border px-2.5 py-1.5 text-left text-[10px] font-semibold text-text-secondary transition-colors hover:border-text-muted/60 hover:text-text-primary disabled:cursor-not-allowed disabled:opacity-40"
                      data-testid="studio-assist-create-layout"
                    >
                      <Wand2 className="h-3.5 w-3.5 shrink-0" aria-hidden />
                      Create starting layout
                    </button>
                  )}
                  {onPackSelection && (
                    <button
                      type="button"
                      disabled={readOnly || selectionCount < 1}
                      onClick={onPackSelection}
                      title={selectionCount < 1
                        ? 'Select components on the canvas to grid-arrange them'
                        : `Grid-arrange the ${selectionCount} selected component${selectionCount === 1 ? '' : 's'}`}
                      className="flex w-full items-center gap-2 rounded-md border border-border px-2.5 py-1.5 text-left text-[10px] font-semibold text-text-secondary transition-colors hover:border-text-muted/60 hover:text-text-primary disabled:cursor-not-allowed disabled:opacity-40"
                      data-testid="studio-assist-pack-selection"
                    >
                      <Grid2x2 className="h-3.5 w-3.5 shrink-0" aria-hidden />
                      Pack selection{selectionCount > 0 ? ` (${selectionCount})` : ''}
                    </button>
                  )}
                  {onConnectRoads && (
                    <button
                      type="button"
                      disabled={readOnly}
                      onClick={onConnectRoads}
                      title="Click an entrance on the canvas, then planning-grade roads connect to all equipment"
                      className="flex w-full items-center gap-2 rounded-md border border-border px-2.5 py-1.5 text-left text-[10px] font-semibold text-text-secondary transition-colors hover:border-text-muted/60 hover:text-text-primary disabled:cursor-not-allowed disabled:opacity-40"
                      data-testid="studio-assist-connect-roads"
                    >
                      <Route className="h-3.5 w-3.5 shrink-0" aria-hidden />
                      Connect with roads
                    </button>
                  )}
                </div>
              </section>
            )}

            <section aria-label="No-build exclusions" className="border-t border-border/70 pt-3" data-testid="studio-layout-exclusions">
              <p className="pb-1 text-[9px] font-bold uppercase tracking-[0.18em] text-text-muted">
                Exclusions{exclusionCount > 0 ? ` · ${exclusionCount}` : ''}
              </p>
              <GenerationLayoutExclusionControls
                layout={activeLayout}
                parcel={testFit.parcel}
                siteLayers={siteLayers}
                sessionKey={sessionKey}
                readOnly={readOnly}
                onPatch={onPatch}
              />
            </section>

            {/* Yield search sweeps ranged parameters — an advanced, opt-in
                pass, so it sits last rather than competing with the everyday
                parcel/rules/actions above. */}
            <section aria-label="Yield search" className="border-t border-border/70 pt-3" data-testid="studio-layout-yield">
              <LayoutParameterSections
                activeLayout={activeLayout}
                readOnly={readOnly}
                onPatch={onPatch}
                search={search}
                sections={['yield']}
                packerInert={packerInert}
              />
            </section>
          </div>
        )}
      </StudioPopover>
    </div>
  );
};
